/**
 * Watching one import run until it stops being one.
 *
 * A chained `setTimeout` rather than `setInterval`: an interval fires whether
 * or not the previous read came back, so a slow answer piles requests up behind
 * itself and the screen ends up describing a state two answers old.
 *
 * THE POLL HAS AN ENDING, and three of them. It stops when the run leaves
 * `running`, it stops when the caller clears the id, and it gives up after a
 * run of consecutive failures rather than asking a vanished run about itself
 * forever. A watcher whose ending nobody wrote is how a settings tab left open
 * overnight becomes a request every two seconds until the tab is closed.
 */
import { useEffect, useRef, useState } from "react";
import { getImportRun, isRunActive, type ImportRunDetail } from "../../../../lib/crmImport";

/** The task asks for two seconds, and an import writes in batches large enough
 *  that a faster poll would mostly re-read the same numbers. */
const POLL_MS = 2000;

/** Consecutive failed reads before the watcher stops asking. */
const MAX_FAILURES = 5;

export interface RunWatch {
  run: ImportRunDetail | null;
  /** The last read did not come back. The run itself may be perfectly fine. */
  stale: boolean;
  /** The watcher stopped asking. Only a reload will move this screen on. */
  gaveUp: boolean;
}

/**
 * Poll `runId` until it finishes.
 *
 * `onFinished` fires once, with the final run, and is where the caller
 * refreshes the counts the run just changed.
 */
export function useImportRun(runId: string | null, onFinished?: (run: ImportRunDetail) => void): RunWatch {
  const [run, setRun] = useState<ImportRunDetail | null>(null);
  const [stale, setStale] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);

  // Held in a ref rather than listed as a dependency: a parent that rebuilds
  // this callback each render would otherwise restart the poll on every render,
  // which is a request per keystroke anywhere else on the page.
  const finished = useRef(onFinished);
  finished.current = onFinished;

  useEffect(() => {
    setRun(null);
    setStale(false);
    setGaveUp(false);
    if (!runId) return;

    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const ctrl = new AbortController();

    const again = () => {
      timer = setTimeout(() => void tick(), POLL_MS);
    };

    const tick = async () => {
      try {
        const next = await getImportRun(runId, ctrl.signal);
        if (!alive) return;
        failures = 0;
        setRun(next);
        setStale(false);
        if (isRunActive(next)) {
          again();
          return;
        }
        finished.current?.(next);
      } catch {
        if (!alive) return;
        failures += 1;
        setStale(true);
        if (failures >= MAX_FAILURES) {
          setGaveUp(true);
          return;
        }
        again();
      }
    };

    void tick();

    return () => {
      alive = false;
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [runId]);

  return { run, stale, gaveUp };
}
